// src/App.jsx
import React, { useState, useEffect, useRef } from 'react';
import { getZonesData } from './data/zones';
import SurfaceHeader from './components/SurfaceHeader';
import ZoneSection from './components/ZoneSection';
import DepthIndicator from './components/DepthIndicator';
import SubmarineIcon from './components/SubmarineIcon';

export default function App() {
  const [zonesData, setZonesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentZone, setCurrentZone] = useState(null);
  const [theme, setTheme] = useState('light');
  const [sliderIndex, setSliderIndex] = useState(0);

  const containerRef = useRef(null);

  // 1) Load zones data on mount
  useEffect(() => {
    getZonesData()
      .then((z) => setZonesData(z))
      .catch(() => setZonesData([]))
      .finally(() => setLoading(false));
  }, []);

  // 2) Scroll listener (always declared)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      const sections = container.querySelectorAll('section');
      let foundId = null;

      sections.forEach((sec) => {
        const rect = sec.getBoundingClientRect();
        if (
          rect.top <= window.innerHeight / 3 &&
          rect.bottom > window.innerHeight / 3
        ) {
          foundId = sec.id;
        }
      });

      if (foundId) {
        const zoneObj = zonesData.find((z) => z.id === foundId);
        setCurrentZone(zoneObj);
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // initialize on first mount

    return () => container.removeEventListener('scroll', onScroll);
  }, [zonesData]);

  // 3) Build version-slider items each render
  const uniqueVersions = Array.from(
    new Set(zonesData.flatMap((z) => z.versions.map((v) => v.versionNumber)))
  ).sort((a, b) => +a - +b);
  const sliderItems = [null, ...uniqueVersions];

  // 4) Theme toggle
  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  return (
    <div
      ref={containerRef}
      className={`${
        theme === 'dark' ? 'dark' : ''
      } h-screen overflow-y-auto snap-y snap-mandatory scroll-smooth`}
    >
      {loading ? (
        // — Show spinner/placeholder while zonesData is loading
        <div className="h-screen flex items-center justify-center">
          <p className="text-xl">Loading zones…</p>
        </div>
      ) : (
        <>
          {/* Surface header (pass allZones so it can look up versions by zone) */}
          <SurfaceHeader
            theme={theme}
            lightBgUrl="/images/light_jungle_background.png"
            darkBgUrl="/images/night_jungle_background.png"
            sliderItems={sliderItems}
            sliderIndex={sliderIndex}
            onSliderChange={setSliderIndex}
            allZones={zonesData}
          />

          {/* Depth indicator HUD (only if we're not on “surface”) */}
          {currentZone && currentZone.id !== 'surface' && (
            <DepthIndicator currentZone={currentZone} theme={theme} />
          )}

          {/* Zone sections (skip “surface”) */}
          {zonesData
            .filter((z) => z.id !== 'surface')
            .map((zone) => (
              <ZoneSection
                key={zone.id}
                zone={zone}
                theme={theme}
                highlightVersion={sliderItems[sliderIndex]}
                allZones={zonesData}
              />
            ))}

          {/* “Back to surface” button */}
          <button
            onClick={() => {
              const surfaceEl = document.getElementById('surface');
              if (surfaceEl) surfaceEl.scrollIntoView({ behavior: 'smooth' });
            }}
            className="fixed bottom-20 right-4 z-50 p-3 text-gray-800 dark:text-yellow-300 bg-white bg-opacity-90 dark:bg-gray-800 rounded-full shadow-lg"
            aria-label="Back to surface"
          >
            ↑
          </button>

          {/* Theme toggle (submarine icon) */}
          <button
            onClick={toggleTheme}
            className="fixed bottom-4 right-4 z-50 p-3 bg-white bg-opacity-90 dark:bg-gray-800 rounded-full shadow-lg"
            aria-label="Toggle light/dark theme"
          >
            <SubmarineIcon className="w-6 h-6 text-gray-800 dark:text-yellow-300" />
          </button>
        </>
      )}
    </div>
  );
}
